import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Car, Users, CalendarCheck, UserCircle, Star, FileText, Instagram, Image, Globe, Settings, LogOut, Search, Plus, Bell, ExternalLink, Menu, X, HandCoins, ClipboardList } from "lucide-react";
import { CSLogo } from "@/components/CSLogo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const nav = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/inventory", icon: Car, label: "Inventory" },
  { to: "/admin/leads", icon: Users, label: "Leads" },
  { to: "/admin/sell-requests", icon: HandCoins, label: "Sell Car Requests" },
  { to: "/admin/test-drives", icon: CalendarCheck, label: "Test Drives" },
  { to: "/admin/customers", icon: UserCircle, label: "Customers" },
  { to: "/admin/reviews", icon: Star, label: "Reviews" },
  { to: "/admin/blog", icon: FileText, label: "Blog" },
  { to: "/admin/instagram", icon: Instagram, label: "Instagram" },
  { to: "/admin/media", icon: Image, label: "Media Library" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const nav2 = useNavigate();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const handleLogout = async () => { await logout(); nav2("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex" data-testid="admin-dashboard">
      {/* SIDEBAR */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#111] text-white flex flex-col transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-[#2A2A2A] flex items-center justify-between">
          <Link to="/admin"><CSLogo variant="light" className="h-9 w-28" /></Link>
          <button onClick={() => setOpen(false)} className="lg:hidden text-gray-400"><X size={18} /></button>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 text-sm overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 transition-colors ${isActive ? "bg-[#940C2C] text-white font-semibold" : "text-gray-400 hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#2A2A2A]">
          <div className="flex items-center gap-2 p-2">
            <div className="h-9 w-9 rounded-full bg-[#940C2C] flex items-center justify-center font-bold text-white text-sm">{(user?.name || user?.email || "A")[0].toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name || "Admin"}</div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider">{user?.role || "admin"}</div>
            </div>
            <button onClick={handleLogout} className="text-gray-400 hover:text-white" data-testid="admin-logout-btn"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* TOPBAR */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3 px-5 py-3">
            <button onClick={() => setOpen(true)} className="lg:hidden p-1.5 rounded hover:bg-gray-100"><Menu size={18} /></button>
            <form onSubmit={(e) => { e.preventDefault(); nav2(`/admin/inventory?search=${encodeURIComponent(query)}`); }}
              className="flex-1 max-w-md flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 focus-within:border-gray-900 focus-within:bg-white transition-colors">
              <Search size={16} className="text-gray-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search cars, leads, customers…" className="flex-1 bg-transparent text-sm" data-testid="admin-global-search" />
            </form>
            <div className="hidden md:flex items-center gap-2 ml-auto">
              <QuickAdd />
              <button className="relative p-2 rounded-full hover:bg-gray-100" title="Notifications">
                <Bell size={18} className="text-gray-700" />
                {stats?.counts?.new_leads > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#940C2C]" />
                )}
              </button>
              <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 border border-gray-200 rounded-full px-3 py-2 hover:border-gray-900">
                <ExternalLink size={13} /> View Site
              </a>
            </div>
          </div>
        </header>

        <main className="flex-1 p-5 lg:p-7">
          <Outlet />
        </main>
      </div>

      {open && <div className="lg:hidden fixed inset-0 bg-black/30 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
};

const QuickAdd = () => {
  const [open, setOpen] = useState(false);
  const items = [
    { to: "/admin/inventory/new", label: "Add Car", icon: Car },
    { to: "/admin/reviews", label: "Add Review", icon: Star },
    { to: "/admin/blog", label: "Add Blog Post", icon: FileText },
    { to: "/admin/instagram", label: "Add Instagram Post", icon: Instagram },
    { to: "/admin/leads", label: "View Leads", icon: ClipboardList },
  ];
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="btn-primary !py-2 !px-3.5 text-xs" data-testid="admin-quick-add">
        <Plus size={14} /> Quick Add
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl py-2 z-30 min-w-[200px]">
            {items.map((i) => (
              <Link key={i.to} to={i.to} onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#940C2C]">
                <i.icon size={14} /> {i.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
